import { useCallback, useEffect, useMemo } from 'react';

import { useLocation, useParams } from 'react-router';
import { Link } from 'react-router-dom';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import usePermissions from 'api/usePermissions';
import { EPublisherSolutionModelSubType } from 'common/contracts';
import { EEventsType, ELocalStorageKeys } from 'constants/enums';
import { useBigQueryEventsTracking } from 'hooks/useBigQueryTracking';
import { usePageTypeByFeatureFlagAndContext } from 'hooks/usePageTypeByFF';
import { localStorageUtil } from 'utils/localStorageUtil';
import { trackMixpanelEvent } from 'utils/mixpanelClient';
import {
  getLandingPageFromUrl,
  getPortalIdForStore,
  getPublisherIdForPortal
} from 'utils/navigationUtil';
import { pageConfigMap, tabLabelsMap } from 'utils/trackingUtils';
import { LastViewedUtils } from 'views/Projects/utils/lastViewedUtils';

import { cn } from 'components/lib/utils';

import { AssetsLibrary, projectTypeRestrictions } from '../constants';
import { useSidebar } from '../context';
import { SidebarMenuButton } from '../elements';
import { useSidebarUrl } from '../hooks';
import {
  ESideBarChipType,
  ESideBarContext,
  ESidebarGroupKey,
  SideBarItem
} from '../types';

import SidebarMenuItemChip from './SidebarMenuItemChip';

interface SidebarItemProps {
  item: SideBarItem;
  groupTitle?: string;
}

export const SidebarMenuItemComponent = ({
  item,
  groupTitle
}: SidebarItemProps) => {
  const { context, isCheckout, userDetails, currentProjectSubType } =
    useSidebar();
  const location = useLocation();
  const { publisherId, portalId } = useParams();
  const { getNavFullUrl } = useSidebarUrl(context);
  const { trackBigQueryEvent } = useBigQueryEventsTracking();
  const { getPermissions } = usePermissions();

  const { pageType } = usePageTypeByFeatureFlagAndContext(item);

  // Function to handle project clicks and update last viewed data
  const handleProjectClick = useCallback(() => {
    if (context === ESideBarContext.COMPANY && item?.id) {
      LastViewedUtils.addLastViewedProject(item.id);
    }
  }, [context, item?.id]);

  const trackSidebarClick = useCallback(() => {
    const isProjectUrl = item.url?.startsWith('project/');
    const isPortalUrl = item.url?.startsWith('portal/');

    const resolvedPortalId =
      portalId ||
      (isPortalUrl ? item.id : undefined);

    const resolvedPublisherId =
      getPublisherIdForPortal(resolvedPortalId, userDetails?.portals) ||
      publisherId ||
      (isProjectUrl ? item.id : undefined);

    const finalPortalId =
      resolvedPortalId ||
      getPortalIdForStore(resolvedPublisherId, userDetails?.portals);

    const landingPage = getLandingPageFromUrl(
      item.url,
      getPermissions,
      userDetails
    );

    const pageConfig = landingPage ? pageConfigMap[landingPage] : undefined;

    const tabName = pageConfig?.title ?? item.title;

    const defaultTabValue =
      pageConfig?.defaultSection ?? item.defaultTab?.[context];

    const section = defaultTabValue
      ? tabLabelsMap[defaultTabValue] ?? defaultTabValue
      : undefined;

    const commonEventData = {
      tab: tabName,
      section,
      role: userDetails?.role,
      userId: userDetails?.userId,
      companyId: userDetails?.company,
      publisherId: resolvedPublisherId,
      portalId: finalPortalId
    };

    trackBigQueryEvent({
      eventName: EEventsType.DASHBOARD_SIDEBAR_CLICKED,
      ...commonEventData
    });

    trackMixpanelEvent(EEventsType.DASHBOARD_SIDEBAR_CLICKED, {
      ...commonEventData,
      user_id: commonEventData.userId,
      company_id: commonEventData.companyId,
      publisher_id: commonEventData.publisherId,
      portal_id: commonEventData.portalId
    });
  }, [
    trackBigQueryEvent,
    item,
    context,
    userDetails,
    getPermissions,
    publisherId,
    portalId
  ]);

  useEffect(() => {
    if (pageType && context === ESideBarContext.COMPANY && item?.id) {
      const existingPageTypes = JSON.parse(
        localStorageUtil.getAny(ELocalStorageKeys.PROJECTS_PAGE_TYPES) || '{}'
      );

      if (!existingPageTypes[item?.id]) {
        existingPageTypes[item?.id] = pageType;
        localStorageUtil.set(ELocalStorageKeys.PROJECTS_PAGE_TYPES, JSON.stringify(existingPageTypes));
      }
    }
  }, [pageType, context, item?.id]);

  // Determine if item should be disabled
  const { isDisabled, chip, conditionalOnClick } = useMemo(() => {
    let isDisabled = item?.isDisabled;
    let chip = item?.chip;
    let conditionalOnClick;
    const seenTabs = JSON.parse(
      localStorageUtil.getAny(ELocalStorageKeys.NEW_MENU_TABS_SEEN) || '[]'
    );

    if (pageType && !seenTabs.includes(item.title)) {
      chip = pageType;
    }

    // Handle checkout restrictions
    if (isCheckout && context === ESideBarContext.PROJECT) {
      const publisherId = location.pathname
        .split('/project/')[1]
        ?.split('/')[0];
      const currentProject = userDetails?.projects?.find(
        (project) => project.publisherId === publisherId
      );

      if (currentProject?.projectType === 'independentCheckout' && groupTitle) {
        const disabledItems =
          projectTypeRestrictions.independentCheckout.disabledItems[
          groupTitle as ESidebarGroupKey
          ];

        const isCurrentItemDisabled = disabledItems?.some(
          (disabledItem: SideBarItem) => disabledItem.title === item.title
        );

        if (
          isCurrentItemDisabled &&
          !(
            currentProjectSubType ===
            EPublisherSolutionModelSubType.PAYMENT_LINKS &&
            item.title === AssetsLibrary.title
          )
        ) {
          isDisabled = true;
        }
      }
    }

    if (chip === ESideBarChipType.NEW) {
      conditionalOnClick = () => {
        trackSidebarClick();
        if (!seenTabs.includes(item.title)) {
          seenTabs.push(item.title);
          localStorageUtil.set(
            ELocalStorageKeys.NEW_MENU_TABS_SEEN,
            JSON.stringify(seenTabs)
          );
        }
        handleProjectClick();
      };
    } else if (chip === ESideBarChipType.COMING_SOON && item.docsLink) {
      conditionalOnClick = () => {
        trackSidebarClick();
        window.open(item.docsLink, '_blank');
      };
    } else {
      conditionalOnClick = () => {
        trackSidebarClick();
        handleProjectClick();
      };
    }

    return { isDisabled, chip, conditionalOnClick };
  }, [
    item?.isDisabled,
    item?.chip,
    item.title,
    item.docsLink,
    pageType,
    isCheckout,
    context,
    location.pathname,
    userDetails?.projects,
    groupTitle,
    currentProjectSubType,
    handleProjectClick,
    trackSidebarClick
  ]);

  const navUrl = useMemo(() =>
    getNavFullUrl(item, groupTitle as ESidebarGroupKey),
    [getNavFullUrl, item, groupTitle]
  );

  const basicContent = (
    <>
      {typeof item.icon === 'string' ? (
        <FontAwesomeIcon className="p-3" icon={cn('fa-regular', item.icon)} />
      ) : (
        item.icon
      )}
      <span className="flex-1 min-w-0 truncate">{item.title}</span>
      <SidebarMenuItemChip type={chip} isDisabled={isDisabled} />
    </>
  );

  return (
    <SidebarMenuButton
      asChild
      isActive={location.pathname.includes(navUrl)}
      isDisabled={isDisabled}
      className="group/sidebar-menu-btn"
    >
      {isDisabled ? (
        <div className="flex items-center gap-2">{basicContent}</div>
      ) : (
        <Link to={navUrl} onClick={conditionalOnClick}>
          {basicContent}
        </Link>
      )}
    </SidebarMenuButton>
  );
};
